import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { uploadFileAndPredict } from '../services/api';
import { readExcelFile } from '../utils/excelUtils';
import JSZip from 'jszip';

const FileUpload = ({ onPredictionComplete, onError, loading, setLoading }) => {
  const onDrop = useCallback(async (acceptedFiles) => {
    console.log('File received:', acceptedFiles[0]);
    console.log('File name:', acceptedFiles[0].name);
    console.log('File type:', acceptedFiles[0].type);
    console.log('File size:', acceptedFiles[0].size);
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setLoading(true);

    try {
      // Check file extension instead of MIME type
      const isZipFile = file.name.toLowerCase().endsWith('.zip') ||
                       file.type === 'application/zip' ||
                       file.type === 'application/x-zip-compressed';
      if (isZipFile) {
        console.log('Processing ZIP file');
        // Handling zip file
        const zip = await JSZip.loadAsync(file);
        console.log('ZIP loaded successfully');
        console.log('Files in ZIP:', Object.keys(zip.files));
        const historicalDataFile = Object.keys(zip.files).find(name => name.includes('historical_data'));
        const predictionsFile = Object.keys(zip.files).find(name => name.includes('tomato_yield_predictions'));

        if (!historicalDataFile || !predictionsFile) {
          throw new Error('Zip file must contain historical_data_***.xlsx and tomato_yield_predictions_***.xlsx files.');
        }

        const historicalDataBlob = await zip.files[historicalDataFile].async('blob');
        const predictionsBlob = await zip.files[predictionsFile].async('blob');

        const historicalData = await readExcelFile(historicalDataBlob);
        const predictionResults = await readExcelFile(predictionsBlob);

        // Pass the data to the parent component
        onPredictionComplete(predictionResults, historicalData);
      } else {
        // Handling xlsx file
        const data = await readExcelFile(file);
        console.log("Data being sent to backend:", data);

        // Validate required columns
        const requiredColumns = ['BasicMaterialNumber', 'SowingMonth', 'Variety', 'ProductionArea'];
        const fileColumns = Object.keys(data[0] || {});
        const missingColumns = requiredColumns.filter(col => !fileColumns.includes(col));

        if (missingColumns.length > 0) {
          throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
        }

        // Send to backend
        const response = await uploadFileAndPredict(file);
        onPredictionComplete(response.predictions, response.historical_data);
      }
    } catch (error) {
      onError(error.message);
    } finally {
      setLoading(false);
    }
  }, [onPredictionComplete, onError, setLoading]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'], // Add this
      'application/octet-stream': ['.zip'] // Sometimes ZIP files are detected as this
    },
    multiple: false,
    disabled: loading
  });

  return (
    <div className="file-upload-section">
      <h2>Upload Input Data</h2>
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? 'active' : ''} ${loading ? 'disabled' : ''}`}
      >
        <input {...getInputProps()} />
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Processing your file...</p>
          </div>
        ) : isDragActive ? (
          <p>Drop the Excel file or Zip file here...</p>
        ) : (
          <div>
            <p>Drag & drop an Excel or Zip file here, or click to select</p>
            <p className="file-requirements">
              Required columns: BasicMaterialNumber, SowingMonth, Variety, ProductionArea
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;